import { Phone, Mail, MapPin, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { gaEvents } from '@/hooks/useGoogleAnalytics';

interface LoanOfficerData {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  logo_url: string | null;
  company_name: string | null;
  city: string | null;
  state: string | null;
  email: string | null;
  phone: string | null;
  license_state: string | null;
  license_number: string | null;
  bio: string | null;
}

interface LoanOfficerCardProps {
  officer: LoanOfficerData;
  compact?: boolean;
  realtorName?: string | null;
  realtorCompany?: string | null;
}

export function LoanOfficerCard({ officer, compact = false, realtorName, realtorCompany }: LoanOfficerCardProps) {
  const fullName = `${officer.first_name} ${officer.last_name}`;
  const location = [officer.city, officer.state].filter(Boolean).join(', ');
  const initials = `${officer.first_name?.[0] || ''}${officer.last_name?.[0] || ''}`;

  const handlePhoneClick = () => {
    if (officer.phone) {
      gaEvents.clickCall(officer.phone);
    }
  };

  const handleEmailClick = () => {
    if (officer.email) {
      gaEvents.clickEmail(officer.email);
    }
  };

  if (compact) {
    return (
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={officer.photo_url || undefined} alt={fullName} className="object-top" />
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold truncate">{fullName}</h4>
              {officer.company_name && (
                <p className="text-sm text-muted-foreground truncate">{officer.company_name}</p>
              )}
              {officer.phone && (
                <a
                  href={`tel:${officer.phone}`}
                  onClick={handlePhoneClick}
                  className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                >
                  <Phone className="h-3 w-3" />
                  {officer.phone}
                </a>
              )}
            </div>
            {officer.logo_url && (
              <img 
                src={officer.logo_url} 
                alt={`${officer.company_name || fullName} logo`}
                className="h-12 w-auto object-contain self-start"
              />
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        {/* Top section: 60/40 split */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left 60% - Officer Info */}
          <div className="flex-[3] flex flex-col sm:flex-row gap-6">
            {/* Photo */}
            <div className="flex flex-col items-center gap-3">
              <Avatar className="h-24 w-24 rounded-md">
                <AvatarImage src={officer.photo_url || undefined} alt={fullName} className="rounded-md object-top" />
                <AvatarFallback className="text-2xl rounded-md">{initials}</AvatarFallback>
              </Avatar>
            </div>

            {/* Info */}
            <div className="flex-1 space-y-1.5">
              <div>
                <h3 className="text-xl font-semibold">{fullName}</h3>
                <p className="text-muted-foreground">Mortgage Loan Officer</p>
                {officer.license_number && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {officer.license_number}
                  </p>
                )}
              </div>

              {officer.company_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{officer.company_name}</span>
                </div>
              )}

              {location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{location}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {officer.phone && (
                  <a
                    href={`tel:${officer.phone}`}
                    onClick={handlePhoneClick}
                    className="text-primary hover:underline flex items-center gap-2 text-sm"
                  >
                    <Phone className="h-4 w-4" />
                    {officer.phone}
                  </a>
                )}
                {officer.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>Submit the form to email {officer.first_name}</span>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Right 40% - Logo */}
          {officer.logo_url && (
            <div className="flex-[2] flex justify-end items-start">
              <img 
                src={officer.logo_url} 
                alt={`${officer.company_name || fullName} logo`}
                className="w-full max-w-[200px] h-auto object-contain"
              />
            </div>
          )}
        </div>

        {/* Bio */}
        {officer.bio && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium mb-2">About {officer.first_name}</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{officer.bio}</p>
            {realtorName && realtorCompany && (
              <p className="text-sm text-primary font-medium mt-4">
                Contact {officer.first_name} and let him know that {realtorName} from {realtorCompany} sent you!
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
